import { useQuery } from '@apollo/client';
import {
  getProduct,
  getProductQueryParams,
  getProductQueryResponse,
} from '@queries/product/queries';
import { Product as IProduct } from '@interfaces';
import { Info } from './components/Info';
import { Products } from '@components/Product';
import { sortedRelatedProducts } from './utils/sortedRelatedProducts';
import { PriceComparator } from './components/PriceComparator';
import { DetailCardContent } from './components/DetailCardContent';
import { PillBox } from './components/PillBox';
import { useEffect, useState } from 'react';
import { ProductJsonLd } from './components/ProductJsonLd';
import { ReferralBannerDiscount } from '@screens/home/landing/ReferralBannerDiscountSection/referralBannerDiscount';
import { useMekiState } from '@hooks/useMekiState';
import { useRouter } from 'next/router';
import {
  ValidateReferenceCouponParams,
  ValidateReferenceCouponQuerie,
  ValidateReferenceCouponResponse,
} from '@queries/referenceCoupon/queries';
import { useAuth } from '@hooks/useAuth';
import { useWindowSize } from '@hooks/useWindowSize';
import { useCartMenu } from '@hooks/useCartMenu';
import { useOpenModal } from '@hooks/useModalOpenState';
import { BackgroundGlobalStyle } from '@components/PageWrapper';
import {
  CarouselContainer,
  DualCardContainer,
  ProductMainCard,
  ProductPageWrapper,
} from './styles';
import { Header } from '@components/Header/Header';
import {
  ProductQueryParams,
  ProductQuery,
  ProductQueryResponse,
} from '@queries/products/queries';
import { Skeleton } from '@mui/material';
import { ProductInteractionLocation } from '@validators/Events/Product';
import { useEventTracking } from '@hooks/useEventTracking';
import { ScreenEventType } from '@validators/Events/Screen';
import { CategoriesBreadcrumbs } from '@components/CategoriesBreadcrumbs';
import { BreadcrumbsContainer } from '@screens/home/landing/styles';

const DualCardContainerSkeleton = () => {
  return (
    <DualCardContainer>
      <ProductMainCard padding="12px 16px">
        <Skeleton variant="rectangular" width={'100%'} height={252} />
      </ProductMainCard>
      <ProductMainCard padding="12px 16px">
        <Skeleton variant="rectangular" width={'100%'} height={252} />
      </ProductMainCard>
    </DualCardContainer>
  );
};

function getValidCategoryAndSubCategorySlug(
  subCategorySlugFromState: string | null | undefined,
  productSubCategories: IProduct['subCategories'],
): { categorySlug?: string; subCategorySlug?: string } {
  const validStateSubCategory = productSubCategories?.find(
    (subCat) => subCat?.slug === subCategorySlugFromState,
  );
  if (validStateSubCategory && subCategorySlugFromState) {
    return {
      categorySlug: validStateSubCategory.category?.slug,
      subCategorySlug: subCategorySlugFromState,
    };
  }

  return {
    categorySlug: productSubCategories?.[0]?.category?.slug,
    subCategorySlug: productSubCategories?.[0]?.slug,
  };
}

interface ProductProps {
  initialProduct?: IProduct;
}
export function ProductPage(props: ProductProps) {
  const router = useRouter();
  const { codigo: code, prodSlug, location: locationFromRouter } = router.query;
  const location = Array.isArray(locationFromRouter)
    ? locationFromRouter[0]
    : locationFromRouter ?? null;

  const { track } = useEventTracking();

  const { appUser: user } = useAuth();
  const { closeModal } = useOpenModal();
  const {
    setReferralCode,
    subCategorySlug: subCategorySlugFromState,
    setCuponAmount,
  } = useMekiState();
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  const { data: { product: freshProduct } = {}, loading: freshProductLoading } =
    useQuery<getProductQueryResponse, getProductQueryParams>(
      getProduct('getFreshProductShow'),
      {
        variables: { params: { slug: prodSlug as string } },
        skip: !prodSlug,
        returnPartialData: true,
      },
    );

  const { categorySlug, subCategorySlug } = getValidCategoryAndSubCategorySlug(
    subCategorySlugFromState,
    freshProduct?.subCategories,
  );

  const {
    data: nonMedicineRelatedProductsData,
    loading: nonMedicineRelatedProductsLoading,
  } = useQuery<ProductQueryResponse, ProductQueryParams>(
    ProductQuery('SearchResults'),
    {
      variables: {
        params: { query: freshProduct?.name, pageNumber: 1 },
      },
      fetchPolicy: 'cache-first',
      skip: freshProduct?.isMedicine,
    },
  );
  const loading = freshProductLoading || nonMedicineRelatedProductsLoading;

  const codeString = code ? decodeURI(code as string) : '';
  const { data: { validateReferenceCoupon: referralUser } = {} } = useQuery<
    ValidateReferenceCouponResponse,
    ValidateReferenceCouponParams
  >(ValidateReferenceCouponQuerie(), {
    variables: { params: { code: codeString.replace(/\s.*/g, '') } },
    fetchPolicy: 'cache-first',
    skip: !codeString.replace(/\s.*/g, ''),
  });

  const product = freshProduct || props.initialProduct;
  const relatedProducts = product?.isMedicine
    ? sortedRelatedProducts(product?.relatedProducts, product?.uuid)
    : sortedRelatedProducts(
        nonMedicineRelatedProductsData?.products.nodes,
        product?.uuid,
      );

  useEffect(() => {
    if (code && referralUser?.isValid) {
      setReferralCode(referralUser.code);
      setCuponAmount(referralUser.amount);
    }
  }, [code, referralUser, setReferralCode, setCuponAmount]);
  useEffect(() => {
    if (loading) return;
    void track(ScreenEventType.ScreenProductView, {
      product_id: product?.id,
      product_name: product?.name,
      product_availability: product?.availability?.status,
      product_brand: (product?.makerBrand as string) ?? product?.laboratory,
      product_price: product?.price ?? null,
      product_prescription_type: product?.prescriptionType ?? null,
      access: location ?? null,
    });
  }, [
    track,
    product?.id,
    product?.laboratory,
    product?.availability,
    product?.name,
    product?.price,
    product?.prescriptionType,
    loading,
    product?.makerBrand,
    location,
  ]);
  const { isDesktop } = useWindowSize();
  const cartMenu = useCartMenu();
  const isCartOpen = cartMenu.isOpen;

  useEffect(() => {
    if (isCartOpen && isDesktop) {
      closeModal();
    }
  }, [isCartOpen, isDesktop, closeModal]);
  const hasRelatedProducts = relatedProducts?.length > 0;

  const title = product?.isMedicine
    ? product.activePrinciple?.split(';').length === 1
      ? `Otros: ${product?.activePrinciple} ${product?.concentration}`
      : `Otras alternativas: ${product?.activePrinciple} `
    : 'Te podría interesar';

  return (
    <>
      <BackgroundGlobalStyle mobileColor="gray" />
      <Header hasSearchBox={true} />
      {product && (
        <ProductPageWrapper>
          <BreadcrumbsContainer>
            <CategoriesBreadcrumbs
              categorySlug={categorySlug}
              subCategorySlug={subCategorySlug}
            />
          </BreadcrumbsContainer>
          <ProductMainCard padding="24px">
            {!user && referralUser?.isValid && (
              <ReferralBannerDiscount
                referenceCouponValidation={referralUser}
                isBannerVisible={isBannerVisible}
                setIsBannerVisible={setIsBannerVisible}
              />
            )}
            <ProductJsonLd product={product} />
            <Info product={product} />
          </ProductMainCard>
          {loading ? (
            <DualCardContainerSkeleton />
          ) : (
            <DualCardContainer>
              <PriceComparator product={product} />
              {hasRelatedProducts && (
                <CarouselContainer>
                  <Products.Carousel
                    data={relatedProducts.map((product) => ({
                      product,
                    }))}
                    loading={loading}
                    title={title}
                    location={ProductInteractionLocation.RelatedProducts}
                  />
                </CarouselContainer>
              )}
              {!hasRelatedProducts && (
                <DetailCardContent.Description product={product} />
              )}
            </DualCardContainer>
          )}
          {hasRelatedProducts && (
            <DetailCardContent.Description
              orientation={isDesktop ? 'horizontal' : 'vertical'}
              product={product}
              loading={loading}
            />
          )}
          <PillBox product={product} />
          <DetailCardContent.Specifications
            product={product}
            loading={loading}
          />
        </ProductPageWrapper>
      )}
    </>
  );
}
